/**
 * Display helpers for the Download Sessions panel.
 *
 * Since EN-1496 a passage download session lists only the passages that
 * download newly stored (Passage.CreatedDate inside the session's window,
 * apex-agm-v1). Re-downloading a range whose passages were all received
 * before (live, or by an earlier download) therefore completes with an empty
 * list. Without a message that looks like the download failed.
 */

export const NO_NEW_PASSAGES_MESSAGE =
  'No new passages: everything in this range was already stored.';

/** RFD FileType for a passage download (3 is raw data). */
export const PASSAGE_DOWNLOAD_FILE_TYPE = 0;

/** The parts of a download session the empty-state check needs. */
export interface DownloadSessionEmptyStateInput {
  downloadSessionId: number;
  fileType: number;
  state: string;
  passageCount: number;
  passages?: unknown[] | null;
  /** Session state when the passage list was last fetched, if ever. */
  passagesLoadedState?: string | null;
}

/**
 * True when a completed passage download has no new passages: the session is
 * real (not the pending placeholder), complete ('C'), a passage download, its
 * final count is 0, and the list fetched after it completed is empty. Requiring
 * a fetch made in state 'C' avoids flashing the message from a list that was
 * fetched (empty) while the download was still running.
 */
export function isCompletedWithNoNewPassages(
  session: DownloadSessionEmptyStateInput | null | undefined
): boolean {
  if (session == null) return false;
  return (
    session.downloadSessionId > 0 &&
    session.state === 'C' &&
    session.fileType === PASSAGE_DOWNLOAD_FILE_TYPE &&
    session.passageCount === 0 &&
    session.passagesLoadedState === 'C' &&
    (session.passages ?? []).length === 0
  );
}

// ------------------------------------------------------------------------
// EN-1517: labels for queued ('Q'), finishing ('R') and Unknown cards.
// ------------------------------------------------------------------------

/** The parts of a card the state label needs. */
export interface DownloadSessionLabelInput {
  downloadSessionId: number;
  state: string;
  unknown?: boolean;
}

export const QUEUED_MESSAGE = 'Queued: waiting to be sent to the device.';
export const UNKNOWN_MESSAGE =
  'No queued download was found for this request. It may have been replaced or never queued.';

/**
 * EN-1517: the line under a cancelled ('A') card. Users can cancel a download
 * now (EN-1515), so a plain "Cancelled" (no "superseded by a newer download
 * or stop command"); a cancel made in this browser shows its own message.
 */
export const CANCELLED_MESSAGE = 'Cancelled';

export function cancelledMessage(s: { cancelMessage?: string | null }): string {
  return s.cancelMessage || CANCELLED_MESSAGE;
}

const isPlaceholder = (s: DownloadSessionLabelInput) =>
  s.downloadSessionId < 0 && s.state !== 'Q';

export function sessionStateLabel(s: DownloadSessionLabelInput): string {
  if (s.unknown) return 'Unknown';
  if (s.state === 'Q') return 'Queued';
  if (isPlaceholder(s)) return 'Requested';
  if (s.state === 'T' || s.state === 'D') return 'Downloading...';
  if (s.state === 'R') return 'Finishing...';
  if (s.state === 'C') return 'Complete';
  if (s.state === 'A') return 'Cancelled';
  return 'Timed Out';
}

export function sessionStateIcon(s: DownloadSessionLabelInput): string {
  if (s.unknown) return 'help_outline';
  if (s.state === 'Q' || isPlaceholder(s)) return 'schedule';
  if (s.state === 'T' || s.state === 'D' || s.state === 'R') return 'sync';
  if (s.state === 'C') return 'check_circle';
  if (s.state === 'A') return 'cancel';
  return 'error_outline';
}

export function sessionStateColor(s: DownloadSessionLabelInput): string {
  if (s.unknown) return 'grey-6';
  if (s.state === 'Q' || isPlaceholder(s)) return 'grey-7';
  if (s.state === 'T' || s.state === 'D' || s.state === 'R')
    return 'light-blue';
  if (s.state === 'C') return 'green';
  if (s.state === 'A') return 'orange';
  return 'red';
}
